import { useEffect, useReducer, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { App, AppStatus, AuthenticationError, AuthorizationError, DeploymentMemoryExceededError, DeploymentLog, DockerfileNotFoundError, NetworkError, Subscription, User, DeploymentStatus, KanikoImageBuildError, ContainerLog, ContainerStats, GitHubResourceNotFoundError, GitRepositoryEmptyError, CreditCardValidationRequiredError } from '@back4app2/sdk';
import MyAppDeploymentStatus from '../components/MyAppDeploymentStatus';
import GitHubReconnectModal from '../components/GitHubReconnectModal';
import Timer from '../components/Timer';
import { timeFromNow } from '../utils/dates';
import buildCommitAuthorsMessage from '../utils/buildCommitAuthorsMessage';
import back4app2 from '../back4app2';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import LogBox from '../components/LogBox';
import Guides from '../components/Guides';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import CancelBuildButton from '../components/CancelBuildButton';
import DockerfileErrorMessage from '../components/CustomErrorMessageUI/DockerfileErrorMessage';
import VerifyEmailErrorMessage from '../components/CustomErrorMessageUI/VerifyEmailErrorMessage';
import SecurityBlockErrorMessage from '../components/CustomErrorMessageUI/SecurityBlockErrorMessage';
import PendingSetupMessage from '../components/CustomErrorMessageUI/PendingSetupMessage';
import OutOfMemoryError from '../components/CustomErrorMessageUI/OutOfMemoryError';
import PaymentPendingErrorMessage from '../components/CustomErrorMessageUI/PaymentPendingErrorMessage';
import SleepingContainerMessage from '../components/CustomErrorMessageUI/SleepingContainerMessage';
import ImageBuildError from '../components/CustomErrorMessageUI/ImageBuildError';
import { getContainerStatsGraphData } from '../utils/metrics';
import ContainerLogBox from '../components/ContainerLogBox';
import { Eyebrow, SegmentedControl, StatCard, StatusDot, StatusPill, SurfaceCard, TileRow } from '../components/ds';
import { BranchIcon, ChevronRightIcon, FileIcon, GlobeIcon, LogsIcon, PortIcon } from '../components/ds/icons';
import { getAppStatusSummary } from '../utils/appStatus';
import ResourceNotAccessibleError from '../components/CustomErrorMessageUI/ResourceNotAccessibleError';
import CreditCardValidationRequiredErrorMessage from '../components/CustomErrorMessageUI/CreditCardValidationRequiredErrorMessage';

interface MyAppOverviewState {
  isLoadingDeploymentLogs: boolean;
  deploymentLogsLoadingErrorMessage?: string;
  deploymentLogs?: DeploymentLog[];
  isLoadingStats: boolean;
  loadingStatsErrorMessage?: string;
  colors?: { [containerId: string]: string };
  cpuGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }};
  ramGraphProps?: { data: { time: number, [containerId: string]: number }[], colors: { [key: string]: string }, max: number};
  isLoadingContainerLogs: boolean;
  containerLogsLoadingErrorMessage?: string;
  containerLogs?: ContainerLog[];
}

const INITIAL_STATE: MyAppOverviewState = {
  isLoadingDeploymentLogs: true,
  isLoadingStats: true,
  isLoadingContainerLogs: true
};

enum MyAppOverviewActionType {
  RESET,
  FINISH_LOADING_DEPLOYMENT_LOGS,
  FINISH_LOADING_STATS,
  FINISH_LOADING_CONTAINER_LOGS,
  RESET_DEPLOYMENT_LOGS,
  RESET_CONTAINER_STATS,
  RESET_CONTAINER_LOGS,
}

const reset = () => ({
  type: MyAppOverviewActionType.RESET
} as const);

const resetDeploymentLogs = () => ({
  type: MyAppOverviewActionType.RESET_DEPLOYMENT_LOGS
} as const);
const resetContainerLogs = () => ({
  type: MyAppOverviewActionType.RESET_CONTAINER_LOGS
} as const);
const resetContainerStats = () => ({
  type: MyAppOverviewActionType.RESET_CONTAINER_STATS
} as const);

const finishLoadingDeploymentLogs = (errorMessage?: string, deploymentLogs?: DeploymentLog[]) => ({
  type: MyAppOverviewActionType.FINISH_LOADING_DEPLOYMENT_LOGS,
  payload: {
    errorMessage,
    deploymentLogs
  }
} as const);

const finishLoadingStats = (errorMessage?: string, stats?: ContainerStats[]) => ({
  type: MyAppOverviewActionType.FINISH_LOADING_STATS,
  payload: {
    errorMessage,
    stats
  }
} as const);

const finishLoadingContainerLogs = (errorMessage?: string, logs?: ContainerLog[]) => ({
  type: MyAppOverviewActionType.FINISH_LOADING_CONTAINER_LOGS,
  payload: {
    errorMessage,
    logs
  }
} as const);

const containerStatsTimeWindow = 1 * 60 * 60 * 1000;

type MyAppOverviewAction = ReturnType<typeof reset> | ReturnType<typeof finishLoadingDeploymentLogs> | ReturnType<typeof finishLoadingContainerLogs>  | ReturnType<typeof finishLoadingStats>  | ReturnType<typeof resetDeploymentLogs>  | ReturnType<typeof resetContainerLogs>  | ReturnType<typeof resetContainerStats>;

const reducer = (state: MyAppOverviewState = INITIAL_STATE, action: MyAppOverviewAction): MyAppOverviewState => {
  switch (action.type) {
    case MyAppOverviewActionType.RESET: 
      return INITIAL_STATE;

    case MyAppOverviewActionType.RESET_DEPLOYMENT_LOGS:
      return {
        ...state,
        isLoadingDeploymentLogs: true,
        deploymentLogsLoadingErrorMessage: undefined,
        deploymentLogs: undefined

      }
    case MyAppOverviewActionType.RESET_CONTAINER_LOGS:
      return {
        ...state,
        isLoadingContainerLogs: true,
        containerLogsLoadingErrorMessage: undefined,
        containerLogs: undefined
      }

    case MyAppOverviewActionType.RESET_CONTAINER_STATS:
      return {
        ...state,
        isLoadingStats: true,
        loadingStatsErrorMessage: undefined,
        colors: undefined,
        cpuGraphProps: undefined,
        ramGraphProps: undefined
      }

    case MyAppOverviewActionType.FINISH_LOADING_DEPLOYMENT_LOGS:
      return {
        ...state,
        isLoadingDeploymentLogs: false,
        deploymentLogsLoadingErrorMessage: action.payload.errorMessage,
        deploymentLogs: action.payload.deploymentLogs
      }
    case MyAppOverviewActionType.FINISH_LOADING_STATS: {
      const { colors,
        cpuGraphProps,
        ramGraphProps,
      } = getContainerStatsGraphData(action.payload.stats || [], state.colors)
      
      return {
        ...state,
        isLoadingStats: false,
        loadingStatsErrorMessage: action.payload.errorMessage,
        colors,
        cpuGraphProps,
        ramGraphProps
      };
    }
    case MyAppOverviewActionType.FINISH_LOADING_CONTAINER_LOGS:
      return {
        ...state,
        isLoadingContainerLogs: false,
        containerLogsLoadingErrorMessage: action.payload.errorMessage,
        containerLogs: action.payload.logs
      };
  }
};

const MyAppOverview = () => {
  const { app, appLoadedAt, user } = useOutletContext<{ app: App, appLoadedAt: Date, user: User }>();
  const mainServiceEnvironment = app && app.mainService && app.mainService.mainServiceEnvironment;
  const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;
  const lastDeployment = app && app.mainService && app.mainService.mainServiceEnvironment && app.mainService.mainServiceEnvironment.lastDeployment;
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  // WHICH failure was waved away, not merely that one was. A page-session flag
  // silenced every later failure too: dismiss it once — and any click outside
  // the modal counts as dismissing — and the next failed deploy offered
  // nothing, on a screen that had just updated itself to show that failure.
  // Reloading was the only way back to the offer.
  const [githubReconnectDismissedFor, setGithubReconnectDismissedFor] = useState<string | undefined>(undefined);
  const navigate = useNavigate();

  // The code, not the log text. The worker writes GITHUB_INSTALLATION_INVALID
  // on the deployment precisely so a screen can branch on it — matching the
  // sentence in the log panel would break the day someone rewords it.
  const needsGithubReconnect =
    lastDeployment?.status === DeploymentStatus.FAILED &&
    lastDeployment?.errorCode === 'GITHUB_INSTALLATION_INVALID';
  const { deploymentLogsLoadingErrorMessage, deploymentLogs, isLoadingDeploymentLogs, containerLogsLoadingErrorMessage, containerLogs, isLoadingContainerLogs, isLoadingStats, loadingStatsErrorMessage, cpuGraphProps, ramGraphProps } = state;

  // Build log and runtime log used to be two nearly identical panels stacked
  // on the same page. One panel, two tabs — nothing is unreachable, and the
  // log gets the width it always needed.
  const [logTab, setLogTab] = useState<'build' | 'runtime'>('build');

  const status = getAppStatusSummary(app);

  /**
   * The last sample per container.
   *
   * The two series do not share a unit: `getContainerStatsGraphData` gives CPU
   * as a percentage of the container's own limit, and RAM as raw bytes. The
   * cards convert; reading either straight out is how the RAM card came to
   * announce "13955072 MB".
   */
  const latestSamples = (
    graph?: { data: { time: number, [containerId: string]: number }[] }
  ): number[] => {
    const rows = graph?.data;
    if (!rows || rows.length === 0) {
      return [];
    }
    const last = rows[rows.length - 1];
    return Object.entries(last)
      .filter(([key]) => key !== 'time')
      .map(([, value]) => value)
      .filter((value): value is number => typeof value === 'number');
  };

  // Percentages do not add up across containers — the busiest one is the
  // number worth showing.
  const cpuSamples = latestSamples(cpuGraphProps);
  const cpuPercent = cpuSamples.length ? Math.max(...cpuSamples) : undefined;

  // Bytes do add up: this is the memory the app is holding in total.
  const ramSamples = latestSamples(ramGraphProps);
  const ramMB = ramSamples.length
    ? Math.round(ramSamples.reduce((sum, value) => sum + value, 0) / (1024 * 1024))
    : undefined;

  const plan = mainServiceEnvironment?.plan;
  const planRAM = plan?.maxRAM
    ? (plan.maxRAM >= 1024 ? `${plan.maxRAM / 1024}GB` : `${plan.maxRAM}MB`)
    : undefined;

  const buildDurationMS = lastDeployment?.deployTask?.elapsedTimeMS;
  const buildDuration = typeof buildDurationMS === 'number'
    ? `${Math.floor(buildDurationMS / 60000)}m ${Math.round((buildDurationMS % 60000) / 1000)}s`
    : '—';


  useEffect(
    () => {
      let subscription: Subscription;

      if (lastDeployment?.id) {
        subscription = back4app2.subscribeToDeploymentLogs(
          lastDeployment.id,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingDeploymentLogs('Network error when loading last deployment logs. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading last deployment logs', error);
                dispatch(finishLoadingDeploymentLogs('Unexpected error when loading last deployment logs. Please try again.'));
              }
            } else {
              dispatch(finishLoadingDeploymentLogs(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          dispatch(resetDeploymentLogs());
        }
      };
    },
    [
      lastDeployment?.id
    ]
  );

  useEffect(() => {
    let statsSubscription: Subscription;
    if (mainServiceEnvironmentId) {
      statsSubscription = back4app2.subscribeToContainersStats(
        mainServiceEnvironmentId,
        containerStatsTimeWindow,
        (error, snapshot) => {
          if (error) {
            if (error instanceof NetworkError) {
              console.error('network error', error);
              dispatch(finishLoadingStats('Network error when loading app stats. Check your internet connection and try again.'));
            } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
              window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
            } else {
              console.error('unexpected error loading app stats', error);
              dispatch(finishLoadingStats('Unexpected error when loading app stats. Please try again.'));
            }
          } else {
            dispatch(finishLoadingStats(undefined, snapshot));
          }
        }
      )
    }
  
    return () => {
      if (statsSubscription) {
        statsSubscription.unsubscribe();          
        dispatch(resetContainerStats());
      }
    }
  }, [mainServiceEnvironmentId]);

  useEffect(
    () => {
      let subscription: Subscription;

      if (mainServiceEnvironmentId) {
        subscription = back4app2.subscribeToContainersLogs(
          mainServiceEnvironmentId,
          (error, snapshot) => {
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoadingContainerLogs('Network error when loading app logs. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else {
                console.error('unexpected error loading app logs', error);
                dispatch(finishLoadingContainerLogs('Unexpected error when loading app logs. Please try again.'));
              }
            } else {
              dispatch(finishLoadingContainerLogs(undefined, snapshot));
            }
          }
        );
      }

      return () => {
        if (subscription) {
          subscription.unsubscribe();
          dispatch(resetContainerLogs());
        }
      };
    },
    [
      mainServiceEnvironmentId
    ]
  );

  useEffect(() => {
    trackEvent(AmplitudeEvent.AT_APP_OVERVIEW_PAGE);
    return () => {
      dispatch(reset())
    }
  }, []);

  return (<>
    {/* Opens itself, once per failure, on the one it can fix. Dismissing
        records THAT deployment's id, so the modal does not come back on every
        re-render of a page that stays open — the failed deployment is still
        the last one, and without this the customer could not read the log
        underneath it. A later deploy that fails the same way is a different
        deployment, so it is offered again, on the open page rather than only
        after a reload. */}
    <GitHubReconnectModal
      open={needsGithubReconnect && githubReconnectDismissedFor !== lastDeployment?.id}
      onClose={() => setGithubReconnectDismissedFor(lastDeployment?.id)}
      serviceEnvironmentId={mainServiceEnvironmentId}
      repositoryId={app?.mainService?.repository?.id}
      repositoryFullName={app?.mainService?.repository?.fullName}
    />

    <div className="flex items-start justify-between gap-6">
      <div className="flex flex-col gap-2 min-w-0">
        {/* The name is on this screen twice already: the sidebar heads with
            CONTAINER / {app.name} at this same size, and the menu row is lit.
            The head names the screen instead. */}
        <h1 className="font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white">
          Overview
        </h1>
      </div>
    </div>

    {/* Four facts on one line. The pair of 143px cards that used to render a
        ghost illustration here cost half the fold to say "no data". */}
    <div className="grid grid-cols-2 gap-3 laptop:grid-cols-4 lg:gap-4">
      <StatCard
        label="status"
        value={status.text}
        tone={status.tone === 'ok' ? 'ok' : status.tone === 'error' ? 'error' : status.tone === 'warn' ? 'warn' : 'value'}
        note={lastDeployment?.createdAt ? timeFromNow(lastDeployment.createdAt) : undefined}
      />
      <StatCard
        label="cpu"
        value={isLoadingStats && mainServiceEnvironmentId ? '…' : (cpuPercent !== undefined ? cpuPercent.toFixed(0) : '—')}
        unit="% of limit"
        tone={cpuPercent === undefined ? 'empty' : 'value'}
        note={loadingStatsErrorMessage
          ? 'stats unavailable'
          : cpuPercent === undefined ? 'no data yet' : `plan limit ${plan?.maxCPU ?? '—'} CPU`}
      />
      <StatCard
        label="ram"
        value={isLoadingStats && mainServiceEnvironmentId ? '…' : (ramMB !== undefined ? ramMB.toString() : '—')}
        unit="MB"
        tone={ramMB === undefined ? 'empty' : 'value'}
        note={loadingStatsErrorMessage
          ? 'stats unavailable'
          : ramMB === undefined ? 'no data yet' : `plan limit ${planRAM ?? '—'}`}
      />
      <StatCard
        label="last build"
        value={buildDuration}
        tone={lastDeployment ? 'value' : 'empty'}
        note={lastDeployment
          ? [lastDeployment.id.slice(0, 8), lastDeployment.branchName].filter(Boolean).join(' · ')
          : 'never deployed'}
      />
    </div>

    {/* The security block is APP state, not deployment state, and it is shown
        ahead of everything else and outside the lastDeployment.error chain
        below. Two reasons: an app blocked by support from the review queue
        has an old successful deployment as its last one, so the deployment
        chain would render nothing at all; and a customer seeing both a
        "blocked" card and an unrelated build error at once cannot tell which
        one matters. */}
    {app.block ? (
      <SecurityBlockErrorMessage
        appId={app.id}
        block={app.block}
        userEmail={user.username}
      />
    ) : app.pendingFirstBuild && app.mainService?.mainServiceEnvironment ? (
      /* A notice with a button, not the form itself. This page is a board of
         panels; a long form dropped into the middle of it pushes everything
         real below the fold, and this is a decision made once rather than a
         permanent part of the page. Same shape as every other state reported
         here — payment pending, unverified email, security block. */
      <PendingSetupMessage
        appId={app.id}
        serviceEnvironment={app.mainService.mainServiceEnvironment}
        pendingFirstBuild={app.pendingFirstBuild}
        onReleased={async () => {
          await back4app2.releaseFirstBuild(
            app.pendingFirstBuild!.serviceEnvironmentId
          );
        }}
      />
    ) : app.status === AppStatus.PENDING_VERIFICATION ? <VerifyEmailErrorMessage userEmail={user.username} /> : (
      <>
        {app.mainService?.mainServiceEnvironment?.isPendingPayment || (!app.mainService?.mainServiceEnvironment?.isFreePlanElegible && app.mainService?.mainServiceEnvironment?.plan.name.includes('Free')) ? (
          <PaymentPendingErrorMessage appId={app.id} mainServiceEnvironment={app.mainService.mainServiceEnvironment} />
        ) : (
          <>
            {lastDeployment?.error ? 
              lastDeployment?.error instanceof DockerfileNotFoundError ? <DockerfileErrorMessage /> 
              : lastDeployment?.error instanceof KanikoImageBuildError ? <ImageBuildError errorMessage={lastDeployment.error.message} /> 
              : lastDeployment?.error instanceof DeploymentMemoryExceededError ? <OutOfMemoryError appId={app.id} /> 
              : lastDeployment?.error instanceof GitHubResourceNotFoundError ? <ResourceNotAccessibleError />
              : lastDeployment?.error instanceof CreditCardValidationRequiredError ? <CreditCardValidationRequiredErrorMessage />
              : lastDeployment?.error instanceof GitRepositoryEmptyError ? null
              : null : null}
            {lastDeployment?.status === DeploymentStatus.SLEEPING ? <SleepingContainerMessage appId={app.id} /> : null}
            <div className="grid gap-5 grid-cols-[minmax(0,1fr)] laptop:grid-cols-[minmax(0,1fr)_380px] laptop:items-start">

              {/* A phone gets the door, not the room. Two log tabs, a source
                  line and forty scrolling lines of machine output was more of
                  this screen than anything else on it, on the surface with the
                  least room to give — and Logs is one tap away in the bar at
                  the bottom. The card itself returns at `laptop`, where it
                  sits beside the deployment column rather than above it. */}
              <button
                type="button"
                onClick={() => navigate(`/apps/${app.id}/logs`)}
                className="b4-focus laptop:hidden w-full text-left rounded-container border border-navy-500 bg-navy-800 hover:bg-navy-700 transition-colors duration-[120ms]"
              >
                <TileRow
                  isFirst
                  tile={<LogsIcon size={18} />}
                  eyebrow="logs"
                  value="Build and runtime output"
                  trailing={<ChevronRightIcon size={16} className="flex-none text-navy-300" />}
                />
              </button>

              <SurfaceCard tone="surface" className="hidden laptop:flex flex-col overflow-hidden">
                <div className="flex-none px-4 py-3 border-b border-navy-700 flex items-center gap-3">
                  <SegmentedControl
                    ariaLabel="Log source"
                    value={logTab}
                    onChange={setLogTab}
                    segments={[
                      { value: 'build', label: 'Build log' },
                      { value: 'runtime', label: 'Runtime log' },
                    ]}
                  />
                </div>
                <div className="flex-none px-4 py-2.5 bg-navy-900 border-b border-navy-700 flex items-center justify-between gap-3">
                  <span className="font-courrier text-ds-115 text-navy-300 truncate">
                    {logTab === 'build'
                      ? (lastDeployment ? `deployment ${lastDeployment.id.slice(0, 8)}` : 'no deployment yet')
                      : 'container output'}
                  </span>
                  <button
                    type="button"
                    onClick={() => navigate(`/apps/${app.id}/logs`)}
                    className="b4-focus flex-none rounded-control text-ds-125 font-semibold text-action-soft hover:text-white"
                  >
                    Open logs
                  </button>
                </div>
                {logTab === 'build' ? (
                  lastDeployment ? (
                    <LogBox
                      errorMessage={deploymentLogsLoadingErrorMessage}
                      logs={deploymentLogs}
                      isLoading={isLoadingDeploymentLogs}
                      canMaximize
                    />
                  ) : (
                    <div className="bg-navy-950 py-10 text-center text-ds-135 text-navy-300">
                      Nothing built yet.
                    </div>
                  )
                ) : (
                  mainServiceEnvironmentId ? (
                    <ContainerLogBox
                      errorMessage={containerLogsLoadingErrorMessage}
                      logs={containerLogs}
                      isLoading={isLoadingContainerLogs}
                      canMaximize
                    />
                  ) : (
                    <div className="bg-navy-950 py-10 text-center text-ds-135 text-navy-300">
                      No container running.
                    </div>
                  )
                )}
              </SurfaceCard>

              <div className="flex flex-col gap-5">
                <SurfaceCard className="p-4 lg:p-5 flex flex-col gap-3.5">
                  <div className="flex items-center justify-between">
                    <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">
                      Last deployment
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate(`/apps/${app.id}/deployments`)}
                      className="b4-focus rounded-control text-ds-125 font-semibold text-action-soft hover:text-white"
                    >
                      All
                    </button>
                  </div>

                  {lastDeployment ? (<>
                    <div className="flex items-center gap-3">
                      <StatusDot tone={status.tone} />
                      <div className="flex-1 min-w-0 flex flex-col gap-[3px]">
                        <span className="font-courrier text-ds-125 text-white truncate" title={lastDeployment.id}>
                          {lastDeployment.id.slice(0, 8)}
                        </span>
                        <span className="text-ds-115 text-navy-200 truncate">
                          {lastDeployment.commitMessage || 'no commit message'}
                        </span>
                      </div>
                      <MyAppDeploymentStatus deployment={lastDeployment} />
                    </div>

                    <div className="flex items-center gap-2 font-courrier text-ds-115 text-navy-300">
                      {/* A deployment from an image has no branch. It shows the
                          digest instead — a tag can point somewhere else by the
                          next deploy and the digest cannot. */}
                      {lastDeployment.imageDigest ? (
                        <span className="truncate" title={lastDeployment.imageDigest}>
                          {lastDeployment.imageDigest.slice(0, 19)}…
                        </span>
                      ) : (<>
                        <BranchIcon size={12} />
                        <span className="truncate">{lastDeployment.branchName}</span>
                      </>)}
                      <span>·</span>
                      <span className="truncate">
                        {(lastDeployment.committedAt && timeFromNow(lastDeployment.committedAt)) || ''}
                        {((lastDeployment.commitAuthorLogin || lastDeployment.commitCommitterLogin)
                          && ` ${buildCommitAuthorsMessage(lastDeployment.commitAuthorLogin, lastDeployment.commitCommitterLogin)}`) || ''}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3 border-t border-navy-500 pt-3">
                      <Timer
                        realElapsedMS={(lastDeployment.deployTask && (lastDeployment.deployTask.elapsedTimeMS + (lastDeployment.deployTask && lastDeployment.deployTask.finishedAt ? 0 : new Date().getTime() - appLoadedAt.getTime()))) || 0}
                        hasFinished={(lastDeployment.deployTask && lastDeployment.deployTask.finishedAt instanceof Date) || false}
                      />
                      <CancelBuildButton deployment={lastDeployment} />
                    </div>
                  </>) : (
                    <span className="text-ds-135 text-navy-300">Nothing deployed yet.</span>
                  )}
                </SurfaceCard>

                {/* The publish drawer's "what will happen", reused for what
                    already happens on every deploy of this app. */}
                {mainServiceEnvironment ? (
                  <SurfaceCard className="p-4 lg:p-5 flex flex-col gap-3.5">
                    <Eyebrow>what happens on deploy</Eyebrow>
                    <div className="rounded-card border border-navy-500 bg-navy-800 overflow-hidden">
                      <TileRow
                        isFirst
                        tile={<FileIcon size={18} />}
                        eyebrow="source"
                        value={app.mainService?.repository
                          ? `GitHub · ${mainServiceEnvironment.branchName}`
                          : `Image · ${mainServiceEnvironment.imageTag || 'latest'}`}
                      />
                      <TileRow
                        tile={<PortIcon size={18} />}
                        eyebrow="port"
                        value={[
                          mainServiceEnvironment.exposedPort ? `${mainServiceEnvironment.exposedPort}/tcp` : 'auto',
                          mainServiceEnvironment.startCommand,
                        ].filter(Boolean).join(' · ')}
                      />
                      <TileRow
                        tile={<GlobeIcon size={18} />}
                        eyebrow="visibility"
                        value={app.block
                          ? 'hidden while under review'
                          : (mainServiceEnvironment.mainCustomDomain?.name || 'no URL yet')}
                        trailing={plan?.name.includes('Free')
                          ? <StatusPill tone="idle">temporary</StatusPill>
                          : undefined}
                      />
                    </div>
                  </SurfaceCard>
                ) : null}
              </div>
            </div>
          </>
        )}
      </>
    )}
    <div className="mt-14">
      <Guides />
    </div>
  </>);
};

export default MyAppOverview;
